// Espace candidat — sections d'évaluation (phase 3, port de
// rec-pipeline.jsx RecCall / RecScorecards / RecScSlider / RecScCard).
// Le masquage anti-ancrage est décidé par le serveur (scorecards: null
// pour un juré qui n'a pas soumis) — le flou ici n'est qu'un décor sur
// des données factices.
import { useState } from 'react';
import {
  REC_ITW_MODES, REC_ITW_TYPES, REC_SC_VERDICTS, recBar, recDimScore, recNeedsEvidence, recWeightedScore,
  type RecGridCrit,
} from '@pointage/shared';
import type {
  RecApplicationDTO, RecInterviewDTO, RecInterviewerDTO, RecRoleDTO, RecScorecardDTO,
} from '../../api/hooks.js';
import { BarCard, DimMeter, GradeGroups } from './V2Ui.js';
import { useRecSt } from './V2Sections.js';
import { useUnsavedGuard } from '../../lib/unsaved.js';

type Grid = RecGridCrit[];

export function recFmtDT(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} · ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Moyenne pondérée du jury sur les scorecards révélées. */
export function recJuryAvg(app: RecApplicationDTO, grid: Grid): { avg: number | null; n: number } {
  const subs = app.interviews.flatMap((i) => i.scorecards ?? (i.myScorecard ? [i.myScorecard] : []));
  const scores = subs
    .map((s) => recWeightedScore(s.scores, grid))
    .filter((x): x is number => x != null);
  if (!scores.length) return { avg: null, n: subs.length };
  return { avg: Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10, n: subs.length };
}

// ── scorecards ──────────────────────────────────────────────
function ScCard({ sc, grid }: { sc: RecScorecardDTO; grid: Grid }) {
  const st = useRecSt();
  const proofs = grid.filter((g) => sc.evidence?.[g.id]);
  const initials = sc.author.split(/\s+/).map((w) => w[0]).slice(0, 2).join('');
  return (
    <div className="rec-sccard">
      <div className="hd">
        <span className="rec-jurypill" style={{ padding: 3 }}><span className="av">{initials}</span></span>
        <div>
          <div className="who">{sc.author}</div>
          <div className="meta">{recFmtDT(sc.at)} · pondérée {recWeightedScore(sc.scores, grid) ?? '—'}/5</div>
        </div>
        <span className={`rec-vd ${sc.verdict}`}>{sc.verdict === 'oui' ? 'OUI' : sc.verdict === 'reserve' ? 'OUI, RÉSERVE' : 'NON'}</span>
      </div>
      <DimMeter s={recDimScore(grid, sc.scores)} st={st} sm />
      <details className="rec-audit">
        <summary>Notes & preuves ({proofs.length})</summary>
        {grid.map((g) => (
          <div key={g.id} className="row"><span className="t">{sc.scores[g.id] ?? '—'}</span><span><b>{g.lbl}</b>{sc.evidence?.[g.id] ? ` — ${sc.evidence[g.id]}` : ''}</span></div>
        ))}
      </details>
      {sc.comment && <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--ink2)', lineHeight: 1.5 }}>« {sc.comment} »</p>}
    </div>
  );
}

function InterviewBlock({ itw, grid, meId, canEdit, busy, onStatus, onScorecard }: {
  itw: RecInterviewDTO;
  grid: Grid;
  meId: string;
  canEdit: boolean;
  busy: boolean;
  onStatus: (id: string, status: 'REALISE' | 'ANNULE' | 'NO_SHOW') => void;
  onScorecard: (itwId: string, scores: Record<string, number>, verdict: 'oui' | 'reserve' | 'non', comment: string, evidence: Record<string, string>) => void;
}) {
  const st = useRecSt();
  const [notes, setNotes] = useState<Record<string, number>>({});
  const [ev, setEv] = useState<Record<string, string>>({});
  const [vd, setVd] = useState<'oui' | 'reserve' | 'non' | null>(null);
  const [com, setCom] = useState('');
  const mustSubmit = itw.amJuror && !itw.myScorecard && itw.status === 'REALISE';
  const subs = itw.scorecards; // null = masqué (je suis juré, pas soumis)
  const filled = grid.filter((g) => notes[g.id]).length;
  const mineS = recDimScore(grid, notes);
  const evMiss = grid.filter((g) => recNeedsEvidence(notes[g.id]) && !(ev[g.id] ?? '').trim()).length;
  useUnsavedGuard({
    id: `rec-scorecard-${itw.id}`,
    dirty: mustSubmit && (filled > 0 || !!vd || com.trim() !== ''),
    label: 'Recrutement · grille d’entretien',
    summary: itw.type,
    save: vd && filled === grid.length && evMiss === 0 && com.trim().length >= 20
      ? async () => onScorecard(itw.id, notes, vd, com, ev) : undefined,
    discard: () => { setNotes({}); setEv({}); setVd(null); setCom(''); },
  });
  const reqs = [
    { ok: filled === grid.length, tx: filled === grid.length ? 'Tous les critères notés' : `${grid.length - filled} critère${grid.length - filled > 1 ? 's' : ''} à noter` },
    { ok: filled > 0 && evMiss === 0, tx: evMiss ? `${evMiss} preuve${evMiss > 1 ? 's' : ''} écrite${evMiss > 1 ? 's' : ''} manquante${evMiss > 1 ? 's' : ''} (notes 1, 2, 5)` : 'Preuves écrites présentes' },
    { ok: !!vd, tx: vd ? 'Verdict choisi' : 'Verdict à choisir' },
    { ok: com.trim().length >= 20, tx: com.trim().length >= 20 ? 'Commentaire global rédigé' : `Commentaire global : encore ${20 - com.trim().length} caractère${20 - com.trim().length > 1 ? 's' : ''}` },
  ];
  const canSubmit = reqs.every((r) => r.ok);
  const doneIds = new Set((subs ?? (itw.myScorecard ? [itw.myScorecard] : [])).map((s) => s.authorId));

  return (
    <div className="rec-itwblock">
      <div className="hd">
        <b>{itw.type}</b>
        <span className="meta">{recFmtDT(itw.scheduledAt)} · {itw.duration} min · {itw.mode}{itw.location ? ` · ${itw.location}` : ''}</span>
        <span className={`rec-itwst ${itw.status}`}>{itw.status === 'PLANIFIE' ? 'Planifié' : itw.status === 'REALISE' ? 'Réalisé' : itw.status === 'ANNULE' ? 'Annulé' : 'No-show'}</span>
      </div>
      <div className="rec-jurypills">
        {itw.interviewers.map((j) => (
          <span key={j.id} className={`rec-jurypill${doneIds.has(j.id) ? ' done' : ''}`} title={doneIds.has(j.id) ? 'Scorecard soumise' : 'Scorecard attendue'}>
            <span className="av">{j.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('')}</span>
            {j.name}{j.id === meId ? ' (vous)' : ''}
          </span>
        ))}
      </div>
      {itw.status === 'PLANIFIE' && canEdit && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button className="rec-go sm" disabled={busy} onClick={() => onStatus(itw.id, 'REALISE')}>Marquer réalisé → scorecards</button>
          <button className="rec-ghost sm" disabled={busy} onClick={() => onStatus(itw.id, 'NO_SHOW')}>No-show</button>
          <button className="rec-ghost sm danger" disabled={busy} onClick={() => onStatus(itw.id, 'ANNULE')}>Annuler</button>
        </div>
      )}
      {itw.kit.length > 0 && (
        <details className="rec-audit">
          <summary>Kit d’entretien du rôle</summary>
          {itw.kit.map((k, i) => <div key={i} className="row"><span className="t">{String(i + 1).padStart(2, '0')}</span><span>{k}</span></div>)}
        </details>
      )}

      {mustSubmit && (
        <div style={{ marginTop: 16 }}>
          <div className="rv2-sc-layout">
            <div>
              <div className="rec-blockh" style={{ marginBottom: 10 }}><span className="n">Scorecard</span><h4>Votre évaluation, dimension par dimension</h4></div>
              <GradeGroups grid={grid} scores={notes} evidence={ev} onScore={(id, v) => setNotes({ ...notes, [id]: v })} onEv={(id, v) => setEv({ ...ev, [id]: v })} />
              <div className="rec-verdict" style={{ marginTop: 12 }}>
                {REC_SC_VERDICTS.map((v, i) => (
                  <button key={v.id} className={`${['go', 'mid', 'stop'][i]}${vd === v.id ? ' on' : ''}`} disabled={busy} onClick={() => setVd(v.id)}>{v.lbl}</button>
                ))}
              </div>
              <div className="rec-field">
                <label>Commentaire global — obligatoire, 20 caractères minimum · {com.trim().length}/20</label>
                <textarea className="rec-ta" rows={3} placeholder="Ce que l’entretien a montré, en faits — jamais lu par le candidat." value={com} onChange={(e) => setCom(e.target.value)}></textarea>
              </div>
            </div>
            <div className="rv2-sc-side">
              <BarCard s={mineS} bar={recBar(mineS, st)} st={st} title="Votre note, en direct" />
              <div className="r4-card" style={{ margin: 0 }}>
                <h4 style={{ color: 'var(--ink3)' }}>Avant de soumettre</h4>
                <div className="rv2-req">{reqs.map((r) => <div key={r.tx} className={r.ok ? 'ok' : ''}><i>{r.ok ? '✓' : ''}</i>{r.tx}</div>)}</div>
                <button className="rec-go" style={{ width: '100%', marginTop: 12 }} disabled={!canSubmit || busy} onClick={() => onScorecard(itw.id, notes, vd!, com.trim(), ev)}>Soumettre la scorecard</button>
              </div>
              <div className="rec-mask" style={{ marginTop: 0 }}>
                <div className="inner">
                  {Array.from({ length: Math.max(itw.scorecardCount, 1) }, (_, i) => (
                    <div key={i}>Membre du jury — {(3 + (i % 3) * 0.7).toFixed(1)}/5 · « évaluation masquée »</div>
                  ))}
                </div>
                <div className="note">
                  <span className="ic">🙈</span>
                  <b>{itw.scorecardCount > 0 ? `${itw.scorecardCount} scorecard${itw.scorecardCount > 1 ? 's' : ''} masquée${itw.scorecardCount > 1 ? 's' : ''}` : 'Aucune scorecard soumise pour l’instant'}</b>
                  <p>Révélation séquentielle : vous verrez les évaluations des autres après avoir soumis la vôtre.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!mustSubmit && subs && subs.length > 0 && (
        <div>{subs.map((s) => <ScCard key={s.id} sc={s} grid={grid} />)}</div>
      )}
      {!mustSubmit && (!subs || !subs.length) && itw.status === 'REALISE' && (
        <div className="rec-emptybox" style={{ marginTop: 12 }}>Entretien réalisé — scorecards en attente du jury.</div>
      )}
    </div>
  );
}

export function InterviewsSection({ app, role, meId, canEdit, busy, onPlanClick, onStatus, onScorecard }: {
  app: RecApplicationDTO;
  role: RecRoleDTO | undefined;
  meId: string;
  canEdit: boolean;
  busy: boolean;
  onPlanClick: () => void;
  onStatus: (id: string, status: 'REALISE' | 'ANNULE' | 'NO_SHOW') => void;
  onScorecard: (itwId: string, scores: Record<string, number>, verdict: 'oui' | 'reserve' | 'non', comment: string, evidence: Record<string, string>) => void;
}) {
  const st = useRecSt();
  const grid = (role?.templates.grid ?? []) as Grid;
  const canPlan = canEdit && (app.stage === 'ENTRETIEN' || app.stage === 'FINALISTE');
  const { n } = recJuryAvg(app, grid);
  const g = app.gates;
  return (
    <div>
      {g.jury && !g.masked && n > 0 && (
        <div style={{ marginBottom: 14 }}>
          <BarCard s={g.jury} bar={g.bar} st={st} title={`Moyenne du jury · ${n} scorecard${n > 1 ? 's' : ''}`} overridden={g.overridden ? app.barOverride?.comment : null} />
        </div>
      )}
      {g.masked && <div className="rec-rule"><span className="ic">🙈</span><span>Moyenne du jury et verdict de barre masqués tant que vous n’avez pas soumis votre scorecard.</span></div>}
      {app.interviews.map((itw) => (
        <InterviewBlock
          key={itw.id} itw={itw} grid={grid} meId={meId} canEdit={canEdit} busy={busy}
          onStatus={onStatus} onScorecard={onScorecard}
        />
      ))}
      {!app.interviews.length && (
        <div className="rec-lockbox">
          <span className="big">🎙</span>
          <h4>Aucun entretien planifié</h4>
          {canPlan ? 'Planifiez le premier entretien — le kit du rôle est pré-chargé pour le jury.'
            : 'Les entretiens se planifient une fois le candidat à l’étape « Entretien ».'}
        </div>
      )}
      {canPlan && (
        <button className="rec-ghost" style={{ marginTop: 4 }} disabled={busy} onClick={onPlanClick}>
          ＋ Planifier un entretien
        </button>
      )}
    </div>
  );
}

// ── modal « Planifier un entretien » (hébergée RecModalHost) ─
export function InterviewModal({ app, role, interviewers, meId, busy, onClose, onSubmit }: {
  app: RecApplicationDTO;
  role: RecRoleDTO | undefined;
  interviewers: RecInterviewerDTO[];
  meId: string;
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: { type: string; scheduledAt: string; duration: number; mode: string; location?: string; interviewerIds: string[] }) => void;
}) {
  const kitTypes = Object.keys(role?.templates.kits ?? {});
  const types = [...new Set([...kitTypes, ...REC_ITW_TYPES])];
  const [type, setType] = useState(types[0] ?? 'Entretien manager');
  const [dt, setDt] = useState('');
  const [duration, setDuration] = useState(60);
  const [mode, setMode] = useState<string>(REC_ITW_MODES[1]);
  const [location, setLocation] = useState('');
  const [jury, setJury] = useState<string[]>([meId]);
  const toggle = (id: string) => setJury((j) => (j.includes(id) ? j.filter((x) => x !== id) : [...j, id]));
  const kit = role?.templates.kits?.[type] ?? [];
  const ok = !!type && !!dt && jury.length > 0;
  return (
    <div className="rec-modal-ov" onClick={onClose}>
      <div className="rec-modal" style={{ width: 'min(640px,94vw)' }} onClick={(e) => e.stopPropagation()}>
        <div className="rec-modal-h">
          <h3>Planifier un entretien — {app.candidate.firstName} {app.candidate.lastName}</h3>
          <button className="rec-x" onClick={onClose}>✕</button>
        </div>
        <div className="rec-modal-b">
          <div className="rec-field">
            <label>Type d’entretien{kit.length > 0 && ` — kit du rôle : ${kit.length} questions`}</label>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              {types.map((t) => <option key={t} value={t}>{t}{role?.templates.kits?.[t] ? ' · kit prêt' : ''}</option>)}
            </select>
          </div>
          <div className="rec-f2">
            <div className="rec-field">
              <label>Date & heure</label>
              <input type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} />
            </div>
            <div className="rec-field">
              <label>Durée</label>
              <select value={duration} onChange={(e) => setDuration(+e.target.value)}>
                {[30, 45, 60, 90, 120].map((d) => <option key={d} value={d}>{d} min</option>)}
              </select>
            </div>
          </div>
          <div className="rec-f2">
            <div className="rec-field">
              <label>Mode</label>
              <select value={mode} onChange={(e) => setMode(e.target.value)}>
                {REC_ITW_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="rec-field">
              <label>{mode === 'visio' ? 'Lien visio' : 'Salle / lieu'} (optionnel)</label>
              <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder={mode === 'visio' ? 'https://meet…' : 'Salle réunion Alger'} />
            </div>
          </div>
          <div className="rec-field">
            <label>Jury — {jury.length} juré{jury.length > 1 ? 's' : ''} · révélation séquentielle des scorecards</label>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {interviewers.map((p) => (
                <button key={p.id} className={`rec-epill${jury.includes(p.id) ? ' on' : ''}`} onClick={() => toggle(p.id)}>
                  {p.name}{p.id === meId ? ' (vous)' : ''}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
            <button className="rec-ghost" onClick={onClose}>Annuler</button>
            <button
              className="rec-go" disabled={!ok || busy}
              onClick={() => onSubmit({
                type,
                scheduledAt: new Date(dt).toISOString(),
                duration, mode,
                ...(location.trim() ? { location: location.trim() } : {}),
                interviewerIds: jury,
              })}
            >Planifier — invitation à envoyer au candidat</button>
          </div>
        </div>
      </div>
    </div>
  );
}
